import {
  defineJourney,
  journeyStep,
  type JourneyStepOptions,
  type StepHandle,
} from '@servicetitan/journey';

import type { AuthUser } from '@modules/AuthModule/types';
import { phoneState } from '@modules/SoftPhoneModule/state';
import waitForWebphoneRegistration from '@modules/SoftPhoneModule/lib/waitForWebphoneRegistration';

const STEP_TIMEOUT_MS = 30_000;
const JOURNEY_TIMEOUT_MS = 60_000;

/**
 * How far ahead of the step timer and the journey limit the webphone wait
 * ends, so its deadline can check the microphone prompt and exclude the run
 * before either timeout marks it bad.
 */
const WEBPHONE_DEADLINE_GRACE_MS = 1_000;

/** `performance.now()` when the journey last started. */
let journeyStartedAt: number | undefined;

/**
 * Critical User Flow "create session": from page load to an agent who is
 * ready to take calls (BFF session, current user, workspace connected and,
 * when the agent can call, the webphone registered).
 *
 * The journey starts once per page load, on `app.started`. Steps run with
 * {@link runCreateSessionStep}, which never reopens a closed journey, so a
 * later re-auth, workspace retry or webphone re-registration is not counted.
 */
export const createSessionJourney = defineJourney({
  name: 'ccpro-ready-to-take-calls',
  team: 'ccp-penguin',
  group: 'contact-center',
  service: 'session',
  timeoutMs: JOURNEY_TIMEOUT_MS,
  stepTimeoutMs: STEP_TIMEOUT_MS,
  expected: ['load-session', 'connect-workspace', 'register-webphone'],
});

export const CreateSessionStep = {
  LoadSession: 'load-session',
  ConnectWorkspace: 'connect-workspace',
  RegisterWebphone: 'register-webphone',
} as const;

export type CreateSessionStepName =
  (typeof CreateSessionStep)[keyof typeof CreateSessionStep];

/**
 * Starts the journey and records when, so the webphone wait can end inside
 * the journey's time limit. Use this instead of `createSessionJourney.start()`.
 */
export function startCreateSessionJourney(): void {
  journeyStartedAt = performance.now();
  createSessionJourney.start();
}

/**
 * Thrown inside a step to mark it and the journey bad for a failure the
 * caller has already handled. {@link runCreateSessionStep} swallows it.
 */
export class CreateSessionStepError extends Error {
  override name = 'CreateSessionStepError';
}

/**
 * Runs `fn` as a step of the create-session journey.
 *
 * Any throw from `fn` marks the step and the journey bad. A
 * {@link CreateSessionStepError} then resolves to `undefined`; other errors
 * are rethrown. When the journey is not open, `fn` still runs with a no-op
 * step handle (`step.journeyName` is `null`).
 */
export async function runCreateSessionStep<T>(
  stepName: CreateSessionStepName,
  fn: (step: StepHandle) => T | Promise<T>,
  opts?: JourneyStepOptions,
): Promise<T | undefined> {
  try {
    // A bare name, unlike the handle, never starts a new journey.
    return await journeyStep(
      createSessionJourney.config.name,
      stepName,
      fn,
      opts,
    );
  } catch (err) {
    if (err instanceof CreateSessionStepError) {
      return undefined;
    }
    throw err;
  }
}

/**
 * Whether the softphone MFE will try to register for this user, leaving out
 * microphone access (see `useCallingDisabled` and `SoftPhoneLoader`).
 */
function canRegisterWebphone(user: AuthUser): boolean {
  if (!user.outboundCallsAllowed) {
    return false;
  }
  return !(user.isImpersonated && phoneState.isUnactivatedImpersonator);
}

/**
 * How long to wait for the webphone: the step limit, or less when the
 * journey limit comes first.
 */
function webphoneWaitMs(): number {
  if (journeyStartedAt === undefined) {
    return STEP_TIMEOUT_MS;
  }
  const remainingMs =
    JOURNEY_TIMEOUT_MS -
    (performance.now() - journeyStartedAt) -
    WEBPHONE_DEADLINE_GRACE_MS;
  return Math.max(0, Math.min(STEP_TIMEOUT_MS, remainingMs));
}

/**
 * Ends the journey once the workspace is connected. Agents who can call must
 * also get the webphone registered.
 */
export async function finishCreateSession(user: AuthUser): Promise<void> {
  if (!canRegisterWebphone(user)) {
    createSessionJourney.complete({ webphone: 'not-expected' });
    return;
  }

  const result = await runCreateSessionStep(
    CreateSessionStep.RegisterWebphone,
    async (step) => {
      const waitMs = webphoneWaitMs();
      const outcome = await waitForWebphoneRegistration({
        timeoutMs: waitMs,
        onStatusChange: (status) =>
          step.setAttribute('connection_status', status),
      });
      if (outcome === 'timeout') {
        step.setAttribute('timeout_ms', waitMs);
        throw new CreateSessionStepError('Webphone did not register');
      }
      return outcome;
    },
    { timeoutMs: STEP_TIMEOUT_MS + WEBPHONE_DEADLINE_GRACE_MS },
  );

  switch (result) {
    case 'registered':
      createSessionJourney.complete({ webphone: 'registered' });
      break;
    case 'unavailable':
      createSessionJourney.complete({ webphone: 'unavailable' });
      break;
    case 'user-gated':
      createSessionJourney.exclude();
      break;
    default:
      // The step failed; the journey is already closed as bad.
      break;
  }
}
